import React from 'react'
import { CaretDownFilled, CaretUpFilled, MinusOutlined } from '@ant-design/icons'
import classnames from 'classnames'
import { formatCountDelta, formatRateDelta, NO_VALUE } from '../math/format'
import { GoodDirection } from '../types'
import './org-dashboard.scss'

type Props = {
    /** A fraction for `count` metrics (0.142 is +14.2%) and percentage points for `rate` metrics. */
    value: number | null
    kind: 'count' | 'rate'
    /** Which direction of change is an improvement. `undefined` is unknown, and reads the same as `none`. */
    goodDirection: GoodDirection | undefined
    steady: boolean
    comparisonLabel: string
    className?: string
    /** A tinted pill with a direction arrow, for a figure that stands beside a headline number. */
    chip?: boolean
}

type Sentiment = 'good' | 'bad' | 'neutral'

const DISPLAYED_DECIMALS = 1

/**
 * Direction and sentiment follow the figure as displayed. A change that rounds to 0.0 reads as no change,
 * so the text, the spoken label and the colour cannot disagree.
 */
const sentimentOf = (displayedValue: number, goodDirection: GoodDirection | undefined): Sentiment => {
    if (goodDirection === undefined || goodDirection === 'none' || displayedValue === 0) return 'neutral'
    return displayedValue > 0 === (goodDirection === 'higher') ? 'good' : 'bad'
}

export const DeltaText: React.FC<Props> = ({
    value,
    kind,
    goodDirection,
    steady,
    comparisonLabel,
    className,
    chip = false,
}) => {
    if (value === null) {
        return (
            <span className={classnames('delta-text', 'delta-none', { 'delta-chip': chip }, className)}>
                {NO_VALUE}
            </span>
        )
    }
    if (steady) {
        return (
            <span
                className={classnames('delta-text', 'delta-steady', { 'delta-chip': chip }, className)}
                role="img"
                aria-label={`steady ${comparisonLabel}`.trim()}
            >
                <MinusOutlined aria-hidden="true" /> Steady
            </span>
        )
    }
    const text = kind === 'rate' ? formatRateDelta(value) : formatCountDelta(value)
    const displayedMagnitude = Number(Math.abs(kind === 'rate' ? value : value * 100).toFixed(DISPLAYED_DECIMALS))
    const displayedValue = value < 0 ? -displayedMagnitude : displayedMagnitude
    const sentiment = sentimentOf(displayedValue, goodDirection)
    const magnitudeLabel = `${displayedMagnitude.toFixed(DISPLAYED_DECIMALS)} ${kind === 'rate' ? 'points' : 'percent'}`
    const direction = value < 0 ? 'down' : 'up'
    const label =
        displayedMagnitude === 0
            ? 'no change'
            : `${direction} ${magnitudeLabel}${sentiment === 'neutral' ? '' : `, ${sentiment}`}`
    const spokenLabel = `${label} ${comparisonLabel}`.trim()
    return (
        <span
            className={classnames('delta-text', `delta-${sentiment}`, { 'delta-chip': chip }, className)}
            role="img"
            aria-label={spokenLabel}
            title={comparisonLabel || undefined}
        >
            {chip ? (
                <>
                    {displayedMagnitude !== 0 &&
                        (value < 0 ? <CaretDownFilled aria-hidden="true" /> : <CaretUpFilled aria-hidden="true" />)}
                    {/* The arrow carries the direction, so the sign is dropped. */}
                    {text.replace(/^[+−-]/, '')}
                </>
            ) : (
                text
            )}
        </span>
    )
}
